import React, { useEffect, useState } from 'react';
import { CALENDAR_VIEWS } from '../helpers';
import SchedulingContext from '../Contexts/SchedulingContext';
import SchedulingToolbar from '../Components/SchedulingToolbar';
import CalendarContainer from '../Components/CalendarContainer';
import { isEmpty } from 'lodash';
import {
  AccountClient,
  GET_CALENDAR_INFO,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION,
  TicketClient,
  useCachedQuery,
  useQuery
} from '@nile/graphql';
import {
  Loader,
  getCurrentUserHook,
  getWeekOfYear,
  useIsAuthorisedHook
} from '@nile/components';
import { useMutation } from '@apollo/client';
import DispatcherView from './DispatcherView';
import dayjs from 'dayjs';
import { Page } from '@superops/ui-design-system';
import {
  useGlobalState,
  clearState
} from '@nile/components/src/Managers/StateManager';

const CalendarPage = (props) => {
  // const INTEGRATED_DATE = dayjs(new Date(2024, 2, 6, 12, 30, 0)); MOCK_DATE
  const DEFAULT_DATE = new Date();
  const user = getCurrentUserHook();
  const [
    selectedDispatcherTechnicians,
    setSelectedDispatcherTechnicians
  ] = useState(props?.dispatcherTechnicians || null);

  const [attendeesFromWorkItem, setAttendeesFromWorkItem] = useState(
    props?.attendeesFromWorkItem || []
  );

  const {
    loading: calendarInfoLoading,
    error: calendarInfoError,
    data: calendarInfo,
    refetch: refetchCalendarInfo
  } = useCachedQuery(GET_CALENDAR_INFO, {
    client: TicketClient
  }); // For integrated date

  const {
    loading: dispatcherSetupLoading,
    error: dispatcherSetupError,
    data: dispatcherSetup,
    refetch: refetchDispatcherTechnicians
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    fetchPolicy: 'cache-and-network',
    variables: {
      userId: user?.userId,
      category: 'schedulingDispatcherTechnicians',
      name: 'schedulingDispatcherTechnicians'
    },
    onCompleted({ getPersonalizationByCategoryAndName }) {
      if (getPersonalizationByCategoryAndName) {
        const value = JSON.parse(getPersonalizationByCategoryAndName?.value);
        const technicians = value?.selectedTechnicians;
        if (!isEmpty(technicians)) {
          setSelectedDispatcherTechnicians(technicians);
        }
      }
    },
    client: AccountClient
  });

  const [savePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient
  });

  const saveDispatcherTechniciansToPersonalisation = (
    technicianData,
    callback
  ) => {
    const personalisedData = {
      name: 'schedulingDispatcherTechnicians',
      category: 'schedulingDispatcherTechnicians',
      value: JSON.stringify({
        selectedTechnicians: technicianData
      }),
      user: {
        firstName: user?.firstName,
        userId: user?.userId,
        email: user?.email,
        lastName: user?.lastName
      }
    };

    savePersonalization({
      variables: personalisedData,
      input: personalisedData
    }).then((res) => {
      refetchDispatcherTechnicians();
      if (callback) {
        callback(res);
      }
    });
  };

  const isDispatcher = useIsAuthorisedHook('dispatcherCalendar.allow');
  const [showSla, setShowSla] = useGlobalState('schedulingShowSla', true);
  const [calendarView, setCalendarView] = useGlobalState(
    'schedulingCalendarView',
    CALENDAR_VIEWS.DAY
  );
  const [selectedDate, setSelectedDate] = useGlobalState(
    'schedulingSelectedDate',
    DEFAULT_DATE
  );
  const [selectedWeekRange, setSelectedWeekRange] = useGlobalState(
    'schedulingSelectedWeekRange',
    null
  );
  const [dispatcherView, setDispatcherView] = useGlobalState(
    'schedulingDispatcherView',
    false
  );
  const [technicianSheetActive, setTechnicianSheetActive] = useState(false);
  const [filterConditions, setFilterConditions] = useGlobalState(
    'schedulingToolbarFilterConditions',
    null
  );

  const toggleTechnicianSheet = () => {
    setTechnicianSheetActive(!technicianSheetActive);
  };

  const handleCalendarViewChange = (view) => {
    setCalendarView(view);
    if (view === CALENDAR_VIEWS.DAY) {
      setSelectedWeekRange(null);
      setSelectedDate(new Date());
    }
    if (view === CALENDAR_VIEWS.WEEK) {
      const selectedDate = dayjs().toDate();
      const weekRange = getWeekOfYear(
        dayjs().year(),
        dayjs(dayjs(selectedDate).format('YYYY-MM-DD')).isoWeek()
      );
      setSelectedDate(selectedDate);
      setSelectedWeekRange(weekRange);
    }
  };

  const onWeekChange = (data) => {
    const formattedDate = dayjs(data?.selectedDate).toDate();
    setSelectedDate(formattedDate);
    setSelectedWeekRange(data?.weekRange);
  };

  if (
    dispatcherSetupLoading &&
    dispatcherView &&
    isEmpty(selectedDispatcherTechnicians)
  ) {
    return (
      <div className="fhp w100">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <SchedulingContext.Provider
        value={{
          showSla: showSla,
          setShowSla: setShowSla,
          calendarView: calendarView,
          setCalendarView: setCalendarView,
          handleCalendarViewChange: handleCalendarViewChange,
          selectedDate: selectedDate,
          setSelectedDate: setSelectedDate,
          isDispatcher: isDispatcher,
          integratedDate:
            calendarInfo?.getUserCalendarInfo?.integratedAt || new Date(),
          onWeekChange: onWeekChange,
          selectedWeekRange: selectedWeekRange,
          setSelectedWeekRange: setSelectedWeekRange,
          dispatcherView: dispatcherView,
          setDispatcherView: setDispatcherView,
          selectedDispatcherTechnicians: selectedDispatcherTechnicians,
          setSelectedDispatcherTechnicians: setSelectedDispatcherTechnicians,
          saveDispatcherTechniciansToPersonalisation: saveDispatcherTechniciansToPersonalisation,
          toggleTechnicianSheet: toggleTechnicianSheet,
          technicianSheetActive: technicianSheetActive,
          filterConditions: filterConditions,
          setFilterConditions: setFilterConditions,
          workItemFromProps: props?.workItem,
          attendeesFromWorkItem: attendeesFromWorkItem,
          setAttendeesFromWorkItem: setAttendeesFromWorkItem,
          integratedCalendarName:
            calendarInfo?.getUserCalendarInfo?.calendarName,
          mixPanelEventSource: props?.mixPanelEventSource,
          calendarInfo: calendarInfo?.getUserCalendarInfo
        }}
      >
        <Page
          title={
            <SchedulingToolbar
              dispatcherLoading={dispatcherSetupLoading}
              onClose={props?.onClose}
              hideCalendarSettings={props?.hideCalendarSettings}
            />
          }
          fullWidth
          variant="secondary"
          contentHeight="90px"
        >
          {dispatcherView ? <DispatcherView /> : <CalendarContainer />}
        </Page>
      </SchedulingContext.Provider>
    </>
  );
};

export default CalendarPage;
